import React from 'react';
import PropTypes from 'prop-types';
import usePayment from 'state/payment/hooks/usePayment';
import useSettings from 'state/settings/hooks/useSettings';
import { cn } from 'lib/utils';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import useStore from 'state/store/hooks/useStore';
import useWebhooks from 'state/webhooks/hooks/useWebhooks';
import usePaymentLinks from 'state/paymentLinks/hooks/usePaymentLinks';
import useStats from 'state/stats/hooks/useStats';
import useAuth from 'state/auth/hooks/useAuth';
import useRefunds from 'state/refunds/hooks/useRefunds';
import useEvents from 'state/events/hooks/useEvents';
import useConsumer from 'state/consumer/hooks/useConsumer';
import useBanking from 'state/banking/hooks/useBanking';
import usePartnerStores from 'state/partnerStores/hooks/usePartnerStores';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import useApplicationCenter from 'state/applicationCenter/hooks/useApplicationCenter';
import useVirtualTerminal from 'state/virtualTerminal/hooks/useVirtualTerminal';
import useUnpaidTickets from 'state/ticket/hooks/useUnpaidTickets';

interface IProps {
  isLoading: number;
}

const GlobalSpinner = ({ isLoading }: IProps): JSX.Element | null => {
  const { auth } = useAuth();
  const { payment } = usePayment();
  const { settings } = useSettings();
  const { organisation } = useOrganisation();
  const { store } = useStore();
  const { webhooks } = useWebhooks();
  const { paymentLinks } = usePaymentLinks();
  const { stats } = useStats();
  const { refunds } = useRefunds();
  const { events } = useEvents();
  const { consumers } = useConsumer();
  const { banking } = useBanking();
  const { partnerStores } = usePartnerStores();
  const { paymentConfig } = usePaymentConfig();
  const { applicationCenter } = useApplicationCenter();
  const { virtualTerminal } = useVirtualTerminal();
  const { ticket } = useUnpaidTickets();
  const loadingSources = [
    isLoading,
    auth?.isLoading,
    payment?.isLoading,
    settings?.isLoading,
    organisation?.isLoading,
    store?.isLoading,
    webhooks?.isLoading,
    paymentLinks.isLoading,
    stats?.isLoading,
    refunds?.isLoading,
    events?.isLoading,
    consumers?.isLoading,
    banking?.isLoading,
    partnerStores?.isLoading,
    applicationCenter.isLoading,
    paymentConfig?.isLoading,
    virtualTerminal?.isLoading,
    ticket?.isLoading,
  ];

  const showSpinner = loadingSources.some((source) => source > 0);

  return showSpinner ? (
    <div
      className={cn(
        'w-screen h-screen fixed inset-0 z-[1231] overflow-hidden flex justify-center items-center bg-[rgba(0,13,34,0.45)]',
        auth?.isNativeMode && 'bg-white'
      )}
    >
      <div
        data-testid="global-spinner"
        role="status"
        aria-label="Loading"
        className="h-10 w-10 animate-spin rounded-full"
        style={{
          borderWidth: 4,
          borderStyle: 'solid',
          // One side transparent creates the spinning gap. Set inline (like
          // LocalSpinner) so the all-sides color can't override the single
          // transparent side via utility cascade order — that produced a
          // gapless ring that looked static while spinning.
          borderColor:
            'var(--color-treez-green) var(--color-treez-green) var(--color-treez-green) transparent',
        }}
      >
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  ) : null;
};

GlobalSpinner.propTypes = {
  isLoading: PropTypes.bool.isRequired,
};
export default GlobalSpinner;
